import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

import "./styles/AdminDashboard.scss";

// Components
import ConfirmationModal from "../../ers/common/components/ConfirmationModal";
import EditDashboardPanel from "./components/EditDashboardPanel";
import Loader from "../../../core/common/loader";
import RenderDashboardCard from "./components/renders/RenderDashboardCard";
import RenderMetricCard from "./components/renders/RenderMetricCard";
import EmptyState from "./components/renders/EmptyState";
import SaveConfigModal from "./components/SaveConfigModal";
import ActionButtons from "./components/ActionButtons";
import FilterSection from "./components/FilterSection";

// Redux actions and selectors
import {
  startEditing,
  saveChanges,
  cancelChanges,
  loadDashboardData,
  saveDashboardConfig,
  selectTopMetricsWithData,
  selectCardsWithData,
  selectIsEditMode,
  selectIsSaving,
  selectCurrentTopMetrics,
  selectCurrentCards,
  ComponentItem,
  loadCompanies,
  setSelectedCompany,
  selectSelectedCompany,
  selectCompanyOptions,
  selectIsLoadingCompanies,
  selectEmployees,
  selectDepartments,
  selectEmployeeOptions,
  selectDepartmentOptions,
  selectDashboardConfigurations,
  selectSelectedConfiguration,
  selectConfigurationOptions,
  selectIsLoadingConfigurations,
  setSelectedConfiguration,
  updateDashboardConfiguration,
  deleteDashboardConfiguration,
  selectIsInitializing,
  selectIsLoadingData,
  loadCompanyRelatedData,
  setGlobalFilters,
  clearGlobalFilters,
} from "../../../core/data/redux/dashboardSlice";

// Utilities
import { notify } from "../../../services/toast.config";
import { all_routes } from "../../router/all_routes";
import { company_short_name } from "../../../environment";
import { truncateCompanyText } from "../../../helper/company_helper";
import BirthdaysWidget from "../../../core/common/birthday/BirthdaysWidget";

export interface ComponentItemWithData extends ComponentItem {
  data: any;
}

const AdminDashboard: React.FC = () => {
  const dispatch = useDispatch<any>();

  // ==================== STATE ====================

  // Modal states
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isSaveModalOpen, setIsSaveModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Save configuration states
  const [configName, setConfigName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [saveMode, setSaveMode] = useState<"update" | "new">("new");
  const [configToDelete, setConfigToDelete] = useState<string | null>(null);

  // Filter states
  const [selectedStartDate, setSelectedStartDate] = useState<moment.Moment | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<moment.Moment | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string>("");
  const [selectedDepartment, setSelectedDepartment] = useState<string>("");

  // ==================== SELECTORS ====================

  // User context
  const user = useSelector((state: any) => state?.user?.user?.userDetails);
  const isEmployee = user?.role_char?.toLowerCase() === "employee";
  const userRoleChar = user?.role_char?.toLowerCase();
  const userDepartmentId = user?.department;

  // Dashboard state
  const {
    topMetricsComponents,
    cardsComponents,
    currentTopMetrics,
    currentCards,
    isEditMode,
    isLoadingData,
    isInitializing,
    isSaving,
    selectedCompany,
    companyOptions,
    isLoadingCompanies,
    employees,
    departments,
    employeeOptions,
    departmentOptions,
    dashboardConfigurations,
    selectedConfiguration,
    configurationOptions,
    isLoadingConfigurations,
  } = useSelector((state: any) => ({
    topMetricsComponents: selectTopMetricsWithData(state, userRoleChar, userDepartmentId) || [],
    cardsComponents: selectCardsWithData(state, userRoleChar, userDepartmentId) || [],
    currentTopMetrics: selectCurrentTopMetrics(state, userRoleChar, userDepartmentId) || [],
    currentCards: selectCurrentCards(state, userRoleChar, userDepartmentId) || [],
    isEditMode: selectIsEditMode(state),
    isLoadingData: selectIsLoadingData(state),
    isInitializing: selectIsInitializing(state),
    isSaving: selectIsSaving(state),
    selectedCompany: selectSelectedCompany(state),
    companyOptions: selectCompanyOptions(state),
    isLoadingCompanies: selectIsLoadingCompanies(state),
    employees: selectEmployees(state),
    departments: selectDepartments(state),
    employeeOptions: selectEmployeeOptions(state),
    departmentOptions: selectDepartmentOptions(state),
    dashboardConfigurations: selectDashboardConfigurations(state),
    selectedConfiguration: selectSelectedConfiguration(state),
    configurationOptions: selectConfigurationOptions(state),
    isLoadingConfigurations: selectIsLoadingConfigurations(state),
  }));

  // ==================== COMPUTED VALUES ====================

  const isPageLoading = useMemo(
    () => isLoadingCompanies || isInitializing,
    [isLoadingCompanies, isInitializing]
  );

  const hasComponents = useMemo(
    () => topMetricsComponents.length > 0 || cardsComponents.length > 0,
    [topMetricsComponents.length, cardsComponents.length]
  );

  const classNames = useMemo(
    () => ({
      content: `content dashboard-content ${isEditMode ? "content-reduced" : ""}`,
      dashboard: `dashboard-container ${isEditMode ? "edit-mode" : ""}`,
      dashboardMain: `dashboard-main ${isEditMode ? "with-panel" : ""}`,
    }),
    [isEditMode]
  );

  const currentFilters = useMemo(() => {
    const filters: any = {};

    if (selectedStartDate && selectedEndDate) {
      filters.date_range = {
        start_date: selectedStartDate.format("YYYY-MM-DD HH:mm:ss"),
        end_date: selectedEndDate.format("YYYY-MM-DD HH:mm:ss"),
      };
    }

    if (selectedEmployee) filters.employee_id = selectedEmployee;
    if (selectedDepartment) filters.department_id = selectedDepartment;

    return filters;
  }, [selectedStartDate, selectedEndDate, selectedEmployee, selectedDepartment]);

  const selectedCompanyOption = useMemo(() => {
    if (!selectedCompany) return null;
    return {
      value: selectedCompany.company_id,
      label: `${truncateCompanyText(selectedCompany.company_name, 20)} - ${selectedCompany.company_type}`,
    };
  }, [selectedCompany]);

  const activeFiltersCount = useMemo(
    () =>
      [selectedEmployee, selectedDepartment, selectedStartDate || selectedEndDate].filter(
        (filter) => filter && filter !== ""
      ).length,
    [selectedEmployee, selectedDepartment, selectedStartDate, selectedEndDate]
  );

  // ==================== EFFECTS ====================

  // Load companies on mount
  useEffect(() => {
    dispatch(loadCompanies({})).catch((error: any) => {
      notify.error(error || "Failed to load companies");
    });
  }, [dispatch]);

  // Load company-related data when company changes
  useEffect(() => {
    if (!selectedCompany?.company_id) return;

    dispatch(loadCompanyRelatedData({ companyInfo: selectedCompany })).catch((error: any) => {
      notify.error(error?.message || "Failed to load company data");
    });
  }, [dispatch, selectedCompany?.company_id, selectedCompany?.company_type]);

  // Sync filters with Redux
  useEffect(() => {
    if (Object.keys(currentFilters).length > 0) {
      dispatch(setGlobalFilters(currentFilters));
    } else {
      dispatch(clearGlobalFilters());
    }
  }, [dispatch, currentFilters]);

  // Load dashboard data when prerequisites are met
  useEffect(() => {
    if (isLoadingConfigurations || !selectedCompany?.company_id || !selectedConfiguration?.id) {
      return;
    }
    dispatch(
      loadDashboardData({
        companyInfo: selectedCompany,
        filters: currentFilters,
      })
    ).catch((error: any) => {
      notify.error(error?.message || "Failed to load dashboard data");
    });
  }, [
    dispatch,
    isLoadingConfigurations,
    selectedCompany?.company_id,
    selectedCompany?.company_type,
    selectedConfiguration?.id,
  ]);

  // ==================== FILTER HANDLERS ====================

  const handleDateChange = useCallback((start: moment.Moment, end: moment.Moment) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);
  }, []);

  const handleEmployeeSelection = useCallback((selectedOption: any) => {
    setSelectedEmployee(selectedOption?.value || "");
  }, []);

  const handleDepartmentSelection = useCallback((selectedOption: any) => {
    setSelectedDepartment(selectedOption?.value || "");
  }, []);

  const handleToggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  const handleApplyFilters = useCallback(() => {
    if (!selectedCompany?.company_id) return;

    dispatch(
      loadDashboardData({
        companyInfo: selectedCompany,
        filters: currentFilters,
        forceRefresh: true,
      })
    );
    notify.success("Filters applied");
  }, [dispatch, selectedCompany, currentFilters]);

  const handleClearFilters = useCallback(() => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setSelectedEmployee("");
    setSelectedDepartment("");
    dispatch(clearGlobalFilters());

    if (selectedCompany?.company_id) {
      dispatch(loadDashboardData({ companyInfo: selectedCompany, forceRefresh: true }));
    }
    notify.info("Filters cleared");
  }, [dispatch, selectedCompany]);

  // ==================== SELECTION HANDLERS ====================

  const handleCompanySelection = useCallback(
    (selectedOption: any) => {
      if (!selectedOption?.value) return;

      const newCompanyData = {
        company_id: selectedOption.value,
        company_name: selectedOption.companyName,
        company_type: selectedOption.companyType,
      };

      dispatch(setSelectedCompany(newCompanyData));

      // Clear filters on company change
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setSelectedEmployee("");
      setSelectedDepartment("");
    },
    [dispatch]
  );

  const handleConfigurationSelection = useCallback(
    (selectedOption: any) => {
      if (!selectedOption?.value) return;

      const configuration = dashboardConfigurations.find(
        (config) => config.id === selectedOption.value
      );

      if (configuration) {
        dispatch(setSelectedConfiguration(configuration));
      }
    },
    [dispatch, dashboardConfigurations]
  );

  const handleSaveModeChange = useCallback(
    (mode: "update" | "new") => {
      setSaveMode(mode);
      if (mode === "update" && selectedConfiguration) {
        setConfigName(selectedConfiguration.name);
        setIsDefault(selectedConfiguration.is_default);
      } else {
        setConfigName("");
        setIsDefault(false);
      }
    },
    [selectedConfiguration]
  );

  // ==================== MODAL HANDLERS ====================

  const handlers = useMemo(
    () => ({
      edit: () => dispatch(startEditing()),

      save: () => {
        if (selectedConfiguration && selectedConfiguration.id !== "-1") {
          setSaveMode("update");
          setConfigName(selectedConfiguration.name);
          setIsDefault(selectedConfiguration.is_default);
        } else {
          setSaveMode("new");
          setConfigName("");
          setIsDefault(false);
        }
        setIsSaveModalOpen(true);
      },

      confirmSave: async () => {
        try {
          if (!configName.trim()) {
            notify.error("Configuration name is required");
            return;
          }

          setIsSaveModalOpen(false);

          if (saveMode === "update" && selectedConfiguration && selectedConfiguration.id !== "-1") {
            await dispatch(
              updateDashboardConfiguration({
                id: selectedConfiguration.id,
                name: configName.trim(),
                dashboard_config: {
                  topMetrics: currentTopMetrics,
                  cards: currentCards,
                },
                is_default: isDefault,
                companyInfo: selectedCompany!,
              })
            ).unwrap();
            notify.success("Dashboard configuration updated successfully");
          } else {
            await dispatch(
              saveDashboardConfig({
                topMetrics: currentTopMetrics,
                cards: currentCards,
                name: configName.trim(),
                is_default: isDefault,
                companyInfo: selectedCompany || undefined,
              })
            ).unwrap();
            notify.success("Dashboard configuration saved successfully");
          }

          dispatch(saveChanges());
          setConfigName("");
          setIsDefault(false);
          setSaveMode("new");
        } catch (error: any) {
          notify.error(error || "Failed to save dashboard configuration");
        }
      },

      close: () => setIsConfirmModalOpen(true),

      cancel: () => {
        setIsConfirmModalOpen(false);
        dispatch(cancelChanges());
      },

      closeModal: () => setIsConfirmModalOpen(false),

      closeSaveModal: () => {
        setIsSaveModalOpen(false);
        setConfigName("");
        setIsDefault(false);
        setSaveMode("new");
      },

      deleteConfig: (id: string) => {
        setConfigToDelete(id);
        setIsDeleteModalOpen(true);
      },

      confirmDelete: async () => {
        if (!configToDelete || !selectedCompany) return;

        try {
          await dispatch(
            deleteDashboardConfiguration({
              id: configToDelete,
              companyInfo: selectedCompany,
            })
          ).unwrap();
          notify.success("Dashboard configuration deleted successfully");
          setIsDeleteModalOpen(false);
          setConfigToDelete(null);
          dispatch(cancelChanges());
        } catch (error: any) {
          notify.error(error || "Failed to delete dashboard configuration");
        }
      },

      closeDeleteModal: () => {
        setIsDeleteModalOpen(false);
        setConfigToDelete(null);
      },
    }),
    [
      dispatch,
      currentTopMetrics,
      currentCards,
      selectedCompany,
      configName,
      isDefault,
      saveMode,
      selectedConfiguration,
      configToDelete,
    ]
  );

  // ==================== RENDER HELPERS ====================

  const renderSection = useCallback(
    (
      components: ComponentItemWithData[],
      renderer: (component: ComponentItemWithData, index: number) => React.ReactElement,
      className: string
    ) => {
      if (!components || components.length === 0) return null;

      return (
        <div className="dashboard-section">
          <div className={`${className} ${isEditMode ? "panel-open" : "panel-closed"}`}>
            {components.map((component, index) => {
              const key = component.id || `${component.type}-${index}`;
              return <React.Fragment key={key}>{renderer(component, index)}</React.Fragment>;
            })}
          </div>
        </div>
      );
    },
    [isEditMode]
  );

  // ==================== RENDER ====================

  if (isPageLoading) return <Loader />;

  return (
    <>
      <div className="page-wrapper">
        <div className={classNames.content}>
          {/* Breadcrumb & Actions */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Dashboard</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Dashboard</li>
                  <li className="breadcrumb-item active">Dashboard</li>
                </ol>
              </nav>
            </div>

            <ActionButtons
              companyOptions={companyOptions}
              selectedCompanyOption={selectedCompanyOption}
              isLoadingCompanies={isLoadingCompanies}
              isEmployee={isEmployee}
              isEditMode={isEditMode}
              isSaving={isSaving}
              selectedCompany={selectedCompany}
              selectedConfiguration={selectedConfiguration}
              onCompanySelection={handleCompanySelection}
              onEdit={handlers.edit}
              onSave={handlers.save}
              onDelete={handlers.deleteConfig}
              onClose={handlers.close}
            />
          </div>

          {!isEditMode && (
            <div className="row">
              <BirthdaysWidget />
            </div>
          )}

          {/* Dashboard Content */}
          <div className={classNames.dashboard}>
            <div className={classNames.dashboardMain}>
              {selectedCompany ? (
                <>
                  {/* Filters */}
                  {!isEditMode && (
                    <FilterSection
                      showFilters={showFilters}
                      configurationOptions={configurationOptions}
                      selectedConfiguration={selectedConfiguration}
                      isLoadingConfigurations={isLoadingConfigurations}
                      selectedCompany={selectedCompany}
                      employeeOptions={employeeOptions}
                      departmentOptions={departmentOptions}
                      employees={employees}
                      departments={departments}
                      isEmployee={isEmployee}
                      selectedStartDate={selectedStartDate}
                      selectedEndDate={selectedEndDate}
                      selectedEmployee={selectedEmployee}
                      selectedDepartment={selectedDepartment}
                      activeFiltersCount={activeFiltersCount}
                      onToggleFilters={handleToggleFilters}
                      onConfigurationSelection={handleConfigurationSelection}
                      onDateChange={handleDateChange}
                      onEmployeeSelection={handleEmployeeSelection}
                      onDepartmentSelection={handleDepartmentSelection}
                      onApplyFilters={handleApplyFilters}
                      onClearFilters={handleClearFilters}
                    />
                  )}

                  {/* Dashboard Data */}
                  {isLoadingData ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ minHeight: "400px" }}
                    >
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading dashboard data...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {renderSection(topMetricsComponents, RenderMetricCard, "metrics-grid")}
                      {renderSection(cardsComponents, RenderDashboardCard, "charts-grid")}
                      {!hasComponents && (
                        <EmptyState selectedCompany={selectedCompany} onEdit={handlers.edit} />
                      )}
                    </>
                  )}
                </>
              ) : (
                <EmptyState selectedCompany={selectedCompany} onEdit={handlers.edit} />
              )}
            </div>

            {/* Edit Panel */}
            {isEditMode && (
              <EditDashboardPanel
                isVisible={isEditMode}
                dashboardName={selectedConfiguration?.name}
                isEmployee={isEmployee}
                userRoleChar={userRoleChar}
                userDepartmentId={userDepartmentId}
              />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © {company_short_name}</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              {company_short_name}
            </Link>
          </p>
        </div>
      </div>

      {/* Save Configuration Modal */}
      <SaveConfigModal
        isOpen={isSaveModalOpen}
        configName={configName}
        isDefault={isDefault}
        saveMode={saveMode}
        isSaving={isSaving}
        hasExistingConfig={selectedConfiguration !== null && selectedConfiguration.id !== "-1"}
        onClose={handlers.closeSaveModal}
        onConfirm={handlers.confirmSave}
        onConfigNameChange={setConfigName}
        onIsDefaultChange={setIsDefault}
        onSaveModeChange={handleSaveModeChange}
      />

      {/* Delete Configuration Modal */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handlers.closeDeleteModal}
        onConfirm={handlers.confirmDelete}
        message="Are you sure you want to delete this dashboard configuration? This action cannot be undone."
        title="Delete Configuration"
        confirmText="Yes, Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Discard Changes Modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={handlers.closeModal}
        onConfirm={handlers.cancel}
        message="Changes made to the dashboard are not saved. Are you sure you want to discard the changes?"
        title="Discard Changes"
        confirmText="Yes, Discard"
        cancelText="Continue Editing"
        type="danger"
      />
    </>
  );
};

export default AdminDashboard;
